import { memo, useEffect, useRef, useState } from "react";
import { Button, Form, Icon, Label, Loader, Message, Modal, Pagination, Table } from "semantic-ui-react";
import type { RunSequence } from "./types/MeBillingType";
import { DEFAULT_ORDER_SUMMARY_FILTER, ORDER_SUMMARY_LIMIT } from "./types/PackageOrderSummaryType";
import type {
  OrderSummaryFilter, OrderSummaryList, PackageSummaryInvoice, PaymentChannel,
} from "./types/PackageOrderSummaryType";
import "./CardMePackageOrderSummary.scss";

const TEXT = {
  th: {
    title: "รายการสั่งซื้อ", channel: "ช่องทางชำระเงิน", all: "ทั้งหมด",
    slip: "ส่งสลิปโอนเงิน", counter: "ชำระหน้าร้าน", unknown: "ไม่ระบุ",
    status: "สถานะชำระเงิน", paid: "ชำระแล้ว", billed: "รอชำระ / รอตรวจสอบ",
    search: "ค้นหา", searchLabel: "เลขที่ใบแจ้งหนี้ / ชื่อลูกค้า", reset: "ล้างตัวกรอง",
    from: "วันที่สร้างรายการ ตั้งแต่", to: "ถึงวันที่", refresh: "รีเฟรช",
    total: "รายการ", invoice: "เลขที่ใบแจ้งหนี้", customer: "ลูกค้า",
    created: "วันที่สร้างรายการ", paidAt: "วันที่รับชำระ", amount: "ยอดเงิน (บาท)",
    details: "รายละเอียด", empty: "ไม่พบรายการสั่งซื้อที่ตรงกับตัวกรอง",
    noDivision: "กรุณาเลือกสาขาเพื่อดูรายการสั่งซื้อ", loading: "กำลังโหลดรายการสั่งซื้อ",
    loadFailed: "ไม่สามารถโหลดรายการสั่งซื้อได้ กรุณาลองใหม่", retry: "ลองใหม่",
    invalidDates: "กรุณาตรวจสอบช่วงวันที่ โดยวันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่มต้น",
    package: "แพ็กเกจ", packageQty: "จำนวนแพ็กเกจ", productCode: "รหัสสินค้า",
    product: "สินค้า / บริการ", unitQty: "จำนวนต่อแพ็กเกจ", totalQty: "จำนวนรวมที่ซื้อ",
    noItems: "ไม่มีรายการสินค้าในแพ็กเกจนี้", noPackages: "ไม่มีรายละเอียดแพ็กเกจ",
    viewSlip: "ดูสลิป", reportedPaidAt: "วันที่ลูกค้าแจ้งโอน", close: "ปิด",
    REQUESTED: "ยังไม่ส่งสลิป", REQUESTED_CONFIRM: "รอตรวจสอบสลิป",
    CONFIRMED: "ยืนยันสลิปแล้ว", REJECTED: "สลิปมีปัญหา", CANCELED: "ยกเลิกแล้ว",
    REQUESTED_REFUND: "ขอคืนเงินแล้ว", REFUNDING: "กำลังคืนเงิน",
    REFUND_REJECTED: "ปฏิเสธการคืนเงิน", REFUNDED: "คืนเงินแล้ว",
    PARTIAL_REFUNDED: "คืนเงินบางส่วนแล้ว",
  },
  en: {
    title: "Orders", channel: "Payment channel", all: "All",
    slip: "Transfer slip", counter: "At counter", unknown: "Unspecified",
    status: "Payment status", paid: "Paid", billed: "Awaiting payment / verification",
    search: "Search", searchLabel: "Invoice number / customer name", reset: "Clear filters",
    from: "Created from", to: "Created to", refresh: "Refresh",
    total: "orders", invoice: "Invoice number", customer: "Customer",
    created: "Created", paidAt: "Payment received", amount: "Amount (THB)",
    details: "Details", empty: "No orders match these filters",
    noDivision: "Select a branch to view orders", loading: "Loading orders",
    loadFailed: "Unable to load orders. Please try again.", retry: "Try again",
    invalidDates: "Check the date range. The end date must be on or after the start date.",
    package: "Package", packageQty: "Package quantity", productCode: "Product code",
    product: "Product / service", unitQty: "Quantity per package", totalQty: "Total purchased",
    noItems: "No items in this package", noPackages: "No package details",
    viewSlip: "View slip", reportedPaidAt: "Customer reported payment", close: "Close",
    REQUESTED: "Awaiting slip", REQUESTED_CONFIRM: "Awaiting slip verification",
    CONFIRMED: "Slip confirmed", REJECTED: "Slip rejected", CANCELED: "Canceled",
    REQUESTED_REFUND: "Refund requested", REFUNDING: "Refund in progress",
    REFUND_REJECTED: "Refund rejected", REFUNDED: "Refunded", PARTIAL_REFUNDED: "Partially refunded",
  },
};

type Props = {
  runSequence: RunSequence;
  orderSummary?: OrderSummaryList;
  selectedDivision?: { id?: number } | null;
  languageUX: "en" | "th";
};

const formatQuantity = (quantity: string | number) => {
  const value = String(quantity ?? "");
  if (!/^\d+(\.\d+)?$/.test(value)) return "—";
  const [whole, fraction = ""] = value.split(".");
  const decimal = fraction.replace(/0+$/, "");
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (decimal ? `.${decimal}` : "");
};

const slipUrl = (value: string | null) => {
  if (!value) return undefined;
  try {
    const url = new URL(value, window.location.origin);
    return ["http:", "https:"].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
};

const CardMePackageOrderSummary = (props: Props) => {
  const text = TEXT[props.languageUX];
  const divisionId = Number(props.selectedDivision?.id);
  const hasDivision = Number.isInteger(divisionId) && divisionId > 0;
  const summary = props.orderSummary?.divisionId === divisionId ? props.orderSummary : undefined;
  const [filter, setFilter] = useState<OrderSummaryFilter>(summary?.filter ?? DEFAULT_ORDER_SUMMARY_FILTER);
  const [invalidDates, setInvalidDates] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<PackageSummaryInvoice | null>(null);
  const latestProps = useRef(props);
  latestProps.current = props;

  useEffect(() => {
    setSelectedInvoice(null);
    setInvalidDates(false);
    if (!Number.isInteger(divisionId) || divisionId <= 0) return;
    const current = latestProps.current;
    const initialFilter = current.orderSummary?.divisionId === divisionId
      ? current.orderSummary.filter : DEFAULT_ORDER_SUMMARY_FILTER;
    setFilter(initialFilter);
    current.runSequence({ sequence: "MeBilling", action: "GET_ORDER_SUMMARY", divisionId,
      filter: initialFilter, activePage: 1 });
  }, [divisionId]);

  const load = (nextFilter: OrderSummaryFilter, activePage = 1) => {
    if (!hasDivision) return;
    setSelectedInvoice(null);
    props.runSequence({ sequence: "MeBilling", action: "GET_ORDER_SUMMARY", divisionId,
      filter: nextFilter, activePage });
  };

  const search = () => {
    const validDate = (value?: string) => !value || (
      /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) &&
      new Date(value).toISOString().slice(0, 10) === value
    );
    if (!validDate(filter.from_date) || !validDate(filter.to_date) ||
        (filter.from_date && filter.to_date && filter.from_date > filter.to_date)) {
      setInvalidDates(true);
      return;
    }
    setInvalidDates(false);
    load(filter);
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "—";
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return "—";
    return new Intl.DateTimeFormat(props.languageUX === "th" ? "th-TH" : "en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      timeZone: "Asia/Bangkok",
    }).format(date);
  };

  const formatAmount = (value?: string) => value != null && Number.isFinite(Number(value))
    ? new Intl.NumberFormat(props.languageUX === "th" ? "th-TH" : "en-GB", {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(value)) : "—";

  const channelName = (channel: PaymentChannel) =>
    channel === "SLIP" ? text.slip : channel === "COUNTER" ? text.counter : "2C2P";
  const channels = (invoice: PackageSummaryInvoice) => invoice.payment_channels.length
    ? invoice.payment_channels.map((channel) => <Label key={channel} size="small">{channelName(channel)}</Label>)
    : text.unknown;
  const paymentStatus = (invoice: PackageSummaryInvoice) => {
    const status = invoice.status_name === "PAID" ? text.paid
      : invoice.status_name === "CANCELED" ? text.CANCELED : text.billed;
    const slipStatus = invoice.slip_status && text[invoice.slip_status as keyof typeof text];
    return <><div>{status}</div>{slipStatus && <small>{slipStatus}</small>}</>;
  };
  const productName = (product: { product_name?: string; product_name_en?: string }) =>
    (props.languageUX === "en" && product.product_name_en) || product.product_name || "—";
  const confirmationUrl = selectedInvoice && slipUrl(selectedInvoice.payment_confirmation);
  const rows = summary?.items ?? [];
  const loading = summary?.loading ?? hasDivision;
  const appliedFilter = summary?.filter ?? DEFAULT_ORDER_SUMMARY_FILTER;

  return (
    <div className="me-package-order-summary">
      <div className="order-summary-heading">
        <h3>{text.title}</h3>
        <Button type="button" basic size="small" disabled={!hasDivision || loading}
          onClick={() => load(appliedFilter, summary?.activePage ?? 1)}>
          <Icon name="refresh" />{text.refresh}
        </Button>
      </div>
      <Form onSubmit={search}>
        <div className="order-summary-filters">
          <Form.Field>
            <label htmlFor="order-summary-search">{text.searchLabel}</label>
            <input id="order-summary-search" value={filter.search ?? ""} maxLength={200}
              onChange={(event) => setFilter({ ...filter, search: event.target.value })} />
          </Form.Field>
          <Form.Field>
            <label htmlFor="order-summary-channel">{text.channel}</label>
            <select id="order-summary-channel" value={filter.payment_channel ?? ""}
              onChange={(event) => setFilter({ ...filter,
                payment_channel: (event.target.value || undefined) as PaymentChannel | undefined })}>
              <option value="">{text.all}</option>
              <option value="SLIP">{text.slip}</option>
              <option value="2C2P">2C2P</option>
              <option value="COUNTER">{text.counter}</option>
            </select>
          </Form.Field>
          <Form.Field>
            <label htmlFor="order-summary-status">{text.status}</label>
            <select id="order-summary-status" value={filter.status ?? ""}
              onChange={(event) => setFilter({ ...filter,
                status: (event.target.value || undefined) as OrderSummaryFilter["status"] })}>
              <option value="">{text.all}</option>
              <option value="PAID">{text.paid}</option>
              <option value="BILLED">{text.billed}</option>
            </select>
          </Form.Field>
          <Form.Field error={invalidDates}>
            <label htmlFor="order-summary-from">{text.from}</label>
            <input id="order-summary-from" type="date" value={filter.from_date ?? ""}
              onChange={(event) => setFilter({ ...filter, from_date: event.target.value })} />
          </Form.Field>
          <Form.Field error={invalidDates}>
            <label htmlFor="order-summary-to">{text.to}</label>
            <input id="order-summary-to" type="date" value={filter.to_date ?? ""}
              onChange={(event) => setFilter({ ...filter, to_date: event.target.value })} />
          </Form.Field>
          <div className="order-summary-filter-actions">
            <Button type="submit" primary loading={loading} disabled={!hasDivision}>{text.search}</Button>
            <Button type="button" disabled={!hasDivision || loading} onClick={() => {
              setFilter(DEFAULT_ORDER_SUMMARY_FILTER);
              setInvalidDates(false);
              load(DEFAULT_ORDER_SUMMARY_FILTER);
            }}>{text.reset}</Button>
          </div>
        </div>
      </Form>
      {invalidDates && <Message negative role="alert">{text.invalidDates}</Message>}
      {!hasDivision && <Message>{text.noDivision}</Message>}
      {summary?.error && <Message negative role="alert">
        <p>{text.loadFailed}</p>
        <Button type="button" size="small" onClick={() => load(appliedFilter, summary.activePage)}>
          {text.retry}
        </Button>
      </Message>}
      {loading && <div className="order-summary-loading" role="status"><Loader active inline="centered" />{text.loading}</div>}
      {hasDivision && !loading && !summary?.error && <>
        <p className="order-summary-count">{summary?.total ?? 0} {text.total}</p>
        <div className="order-summary-table">
          <Table celled selectable unstackable>
            <Table.Header><Table.Row>
              <Table.HeaderCell>{text.invoice}</Table.HeaderCell>
              <Table.HeaderCell>{text.customer}</Table.HeaderCell>
              <Table.HeaderCell>{text.created}</Table.HeaderCell>
              <Table.HeaderCell>{text.channel}</Table.HeaderCell>
              <Table.HeaderCell>{text.status}</Table.HeaderCell>
              <Table.HeaderCell>{text.amount}</Table.HeaderCell>
              <Table.HeaderCell>{text.paidAt}</Table.HeaderCell>
              <Table.HeaderCell>{text.details}</Table.HeaderCell>
            </Table.Row></Table.Header>
            <Table.Body>
              {rows.map((invoice) => <Table.Row key={invoice.id}>
                <Table.Cell>{invoice.code || "—"}</Table.Cell>
                <Table.Cell>{invoice.patient_name || "—"}</Table.Cell>
                <Table.Cell>{formatDate(invoice.created)}</Table.Cell>
                <Table.Cell>{channels(invoice)}</Table.Cell>
                <Table.Cell>{paymentStatus(invoice)}</Table.Cell>
                <Table.Cell textAlign="right">{formatAmount(invoice.price)}</Table.Cell>
                <Table.Cell>{formatDate(invoice.paid_at)}</Table.Cell>
                <Table.Cell><Button type="button" size="small" onClick={() => setSelectedInvoice(invoice)}>
                  {text.details}
                </Button></Table.Cell>
              </Table.Row>)}
              {!rows.length && <Table.Row><Table.Cell colSpan={8} textAlign="center">{text.empty}</Table.Cell></Table.Row>}
            </Table.Body>
          </Table>
        </div>
        {!!summary?.total && <div className="order-summary-pagination">
          <Pagination activePage={summary.activePage} totalPages={Math.ceil(summary.total / ORDER_SUMMARY_LIMIT)}
            onPageChange={(_event, { activePage }) => load(appliedFilter, Number(activePage))} />
        </div>}
      </>}
      <Modal open={!!selectedInvoice} onClose={() => setSelectedInvoice(null)} closeIcon size="large"
        className="order-summary-detail" role="dialog" aria-modal="true" aria-labelledby="order-summary-detail-title">
        <Modal.Header id="order-summary-detail-title">{text.details} {selectedInvoice?.code}</Modal.Header>
        <Modal.Content scrolling>
          {selectedInvoice && <>
            <div className="order-summary-detail-info">
              <div><strong>{text.customer}:</strong> {selectedInvoice.patient_name || "—"}</div>
              <div><strong>{text.channel}:</strong> {channels(selectedInvoice)}</div>
              <div><strong>{text.status}:</strong> {paymentStatus(selectedInvoice)}</div>
              <div><strong>{text.amount}:</strong> {formatAmount(selectedInvoice.price)}</div>
              <div><strong>{text.paidAt}:</strong> {formatDate(selectedInvoice.paid_at)}</div>
              {selectedInvoice.patient_paid_at && <div><strong>{text.reportedPaidAt}:</strong> {formatDate(selectedInvoice.patient_paid_at)}</div>}
              {confirmationUrl && <a href={confirmationUrl} target="_blank" rel="noopener noreferrer">{text.viewSlip}</a>}
            </div>
            {selectedInvoice.orders.map((order) => <section key={order.id} className="order-summary-package">
              <h4>{text.package}: {productName(order)} <small>({text.packageQty}: {formatQuantity(order.quantity)})</small></h4>
              <div className="order-summary-table"><Table celled unstackable>
                <Table.Header><Table.Row>
                  <Table.HeaderCell>{text.productCode}</Table.HeaderCell>
                  <Table.HeaderCell>{text.product}</Table.HeaderCell>
                  <Table.HeaderCell>{text.unitQty}</Table.HeaderCell>
                  <Table.HeaderCell>{text.totalQty}</Table.HeaderCell>
                </Table.Row></Table.Header>
                <Table.Body>
                  {order.items.map((item) => <Table.Row key={item.id}>
                    <Table.Cell>{item.product_code || "—"}</Table.Cell>
                    <Table.Cell>{productName(item)}</Table.Cell>
                    <Table.Cell textAlign="right">{formatQuantity(item.quantity)}</Table.Cell>
                    <Table.Cell textAlign="right">{formatQuantity(item.ordered_quantity)}</Table.Cell>
                  </Table.Row>)}
                  {!order.items.length && <Table.Row><Table.Cell colSpan={4}>{text.noItems}</Table.Cell></Table.Row>}
                </Table.Body>
              </Table></div>
            </section>)}
            {!selectedInvoice.orders.length && <Message>{text.noPackages}</Message>}
          </>}
        </Modal.Content>
        <Modal.Actions><Button type="button" onClick={() => setSelectedInvoice(null)}>{text.close}</Button></Modal.Actions>
      </Modal>
    </div>
  );
};

export default memo(CardMePackageOrderSummary);
